import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  sendGetClientRequest,
  sendPatchClientRequest,
} from "../../../api/helper";
import { serialize } from "../../../utils";

const initialState = {
  rows: [],
  total: 0,
  page: 1,
  limit: 10,
  loading: false,
  error: null,
  filter: {
    search: "",
    status: "",
    sort_dir: "desc", // 送出時間排序：desc=由新到舊（預設）/ asc=由舊到新
  },
  detail: null,
  detailLoading: false,
};

const buildQuery = ({ filter = {}, page, limit }) => {
  const q = {};
  if (filter.search) q.search = filter.search;
  if (filter.status) q.status = filter.status;
  if (filter.sort_dir) q.sort_dir = filter.sort_dir;
  if (page) q.page = page;
  if (limit) q.limit = limit;
  return Object.keys(q).length ? `?${serialize(q)}` : "";
};

// 客製化訂單所有 API 都回傳統一信封：{ error, msg, data }
const unwrapOrThrow = (response) => {
  if (!response || response.error) {
    const err = new Error(response?.msg || "API error");
    err.payload = response?.data;
    throw err;
  }
  return response.data;
};

// GET /api/customized-orders → data: { rows, total, limit, page }
// 每一筆 row 會帶 details[]（含 price）與 ai_details[]（含 price），供前端加總「應付總額」
export const fetchCustomizedOrders = createAsyncThunk(
  "customizedOrder/fetchList",
  async ({ filter, page, limit } = {}) => {
    const qs = buildQuery({ filter, page, limit });
    const response = await sendGetClientRequest(`/api/customized-orders${qs}`);
    return unwrapOrThrow(response) ?? { rows: [], total: 0 };
  }
);

// GET /api/customized-orders/{id} → data: { ...order, details[].plan, ai_details[] }
export const fetchCustomizedOrderDetail = createAsyncThunk(
  "customizedOrder/fetchDetail",
  async (id) => {
    const response = await sendGetClientRequest(
      `/api/customized-orders/${id}`
    );
    return unwrapOrThrow(response);
  }
);

// PATCH /api/customized-orders/{id}/status → data: { ...order }
// status: 'pending' | 'in_progress' | 'signed' | 'complete'
export const updateCustomizedOrderStatus = createAsyncThunk(
  "customizedOrder/updateStatus",
  async ({ id, status }) => {
    const response = await sendPatchClientRequest(
      `/api/customized-orders/${id}/status`,
      { status }
    );
    return unwrapOrThrow(response);
  }
);

// 更新後把最新的 order 同步回列表與 detail，避免多打一次列表 API
const syncOrderEverywhere = (state, order) => {
  if (!order || order.id == null) return;
  state.rows = state.rows.map((row) =>
    row.id === order.id ? { ...row, ...order } : row
  );
  if (state.detail && state.detail.id === order.id) {
    state.detail = { ...state.detail, ...order };
  }
};

const customizedOrderSlice = createSlice({
  name: "customizedOrder",
  initialState,
  reducers: {
    setFilter: (state, { payload }) => {
      state.filter = { ...state.filter, ...payload };
      state.page = 1;
    },
    setPage: (state, { payload }) => {
      state.page = payload;
    },
    resetDetail: (state) => {
      state.detail = null;
    },
  },
  extraReducers: {
    [fetchCustomizedOrders.pending]: (state) => {
      state.loading = true;
      state.error = null;
    },
    [fetchCustomizedOrders.fulfilled]: (state, { payload }) => {
      state.loading = false;
      state.rows = Array.isArray(payload?.rows) ? payload.rows : [];
      state.total = Number.isFinite(payload?.total) ? payload.total : 0;
      if (Number.isInteger(payload?.page) && payload.page > 0) {
        state.page = payload.page;
      }
      if (Number.isInteger(payload?.limit) && payload.limit > 0) {
        state.limit = payload.limit;
      }
    },
    [fetchCustomizedOrders.rejected]: (state, { error }) => {
      state.loading = false;
      state.error = error?.message ?? "fetch failed";
    },

    [fetchCustomizedOrderDetail.pending]: (state) => {
      state.detailLoading = true;
    },
    [fetchCustomizedOrderDetail.fulfilled]: (state, { payload }) => {
      state.detailLoading = false;
      state.detail = payload;
    },
    [fetchCustomizedOrderDetail.rejected]: (state) => {
      state.detailLoading = false;
    },

    [updateCustomizedOrderStatus.fulfilled]: (state, { payload }) => {
      syncOrderEverywhere(state, payload);
    },
  },
});

export const { setFilter, setPage, resetDetail } = customizedOrderSlice.actions;
export const selectCustomizedOrder = (state) => state.customizedOrder;
export default customizedOrderSlice.reducer;
